import { useEffect, useCallback, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { observer } from '@react';
import { useAuth } from '@react/hooks';
import { TabWorkspace } from '@/components/tabs/TabWorkspace';
import { Sidebar } from './Sidebar';
import { Navbar } from './Navbar';
import { RightRail } from './RightRail';
import { MobileSidebar } from './MobileSidebar';
import { SearchModal } from '@/components/SearchModal';
import { QuickCreate } from '@/components/QuickCreate';
import { AppLauncher } from '@/components/AppLauncher';
import { TenantSwitcher } from '@/components/TenantSwitcher';
import { sidebarStore } from '@/stores/SidebarStore';
import { railStore } from '@/stores/RailStore';
import { searchStore } from '@/stores/SearchStore';
import { ZONE2_SHORTCUT_HREFS } from '@/features/layout/logic';
import { NavbarSlotProvider } from '@/features/tabs/PaneChrome';
import { useSettingsInit } from '@/hooks/useSettingsInit';

export const AppShell = observer(() => {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Load and apply user UI settings (theme, locale, density) at boot
  useSettingsInit();

  const [quickCreateOpen, setQuickCreateOpen] = useState(false);
  const [launcherOpen, setLauncherOpen] = useState(false);
  const [tenantSwitcherOpen, setTenantSwitcherOpen] = useState(false);
  const [chordPending, setChordPending] = useState<string | null>(null);
  const chordTimer = useRef<number>();
  // The navbar's slot for the open page's controls. It is held here because the
  // navbar publishes it and the panes below consume it, and the shell is where
  // those two meet — they are siblings everywhere else.
  const [navbarSlot, setNavbarSlot] = useState<HTMLElement | null>(null);

  // Save last URL for landing-after-login
  useEffect(() => {
    try {
      localStorage.setItem('lastUrl', location.pathname + location.search);
    } catch {
      // localStorage unavailable
    }
  }, [location.pathname, location.search]);

  // Global keyboard shortcuts
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    const meta = e.metaKey || e.ctrlKey;

    // Don't capture when typing in inputs
    const target = e.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
      if (e.key === 'Escape') {
        // Allow escape even in inputs
      } else {
        return;
      }
    }

    // Chord shortcuts: M+R (mark related), M+B (mark blocker)
    if (chordPending === 'm') {
      clearTimeout(chordTimer.current);
      setChordPending(null);
      if (e.key === 'r') {
        e.preventDefault();
        railStore.setTab('resources');
        if (!railStore.isOpen) railStore.toggle();
        (window as any).__railOpenResourceMenu?.('RELATION_TYPE_REFERENCES');
        return;
      } else if (e.key === 'b') {
        e.preventDefault();
        railStore.setTab('resources');
        if (!railStore.isOpen) railStore.toggle();
        (window as any).__railOpenResourceMenu?.('RELATION_TYPE_BLOCKS');
        return;
      }
    }
    if (!meta && e.key === 'm' && !chordPending) {
      setChordPending('m');
      chordTimer.current = window.setTimeout(() => setChordPending(null), 500);
      return;
    }

    if (meta && e.key === 'k') {
      e.preventDefault();
      searchStore.open();
    } else if (meta && e.key === 'n') {
      e.preventDefault();
      setQuickCreateOpen(true);
    } else if (meta && e.key === 'b') {
      e.preventDefault();
      sidebarStore.toggleCollapse();
    } else if (meta && e.key === '.') {
      e.preventDefault();
      railStore.toggle();
    } else if (meta && e.shiftKey && e.key === 'A') {
      e.preventDefault();
      setLauncherOpen(true);
    } else if (meta && e.shiftKey && (e.key === 'I' || e.key === 'i')) {
      e.preventDefault();
      navigate('/inbox');
    } else if (meta && e.key >= '1' && e.key <= '7') {
      const idx = parseInt(e.key, 10) - 1;
      if (idx < ZONE2_SHORTCUT_HREFS.length) {
        e.preventDefault();
        navigate(ZONE2_SHORTCUT_HREFS[idx]);
      }
    } else if (e.key === 'Escape') {
      if (searchStore.isOpen) {
        searchStore.close();
      } else if (launcherOpen) {
        setLauncherOpen(false);
      } else if (quickCreateOpen) {
        setQuickCreateOpen(false);
      } else if (tenantSwitcherOpen) {
        setTenantSwitcherOpen(false);
      } else if (railStore.isOpen) {
        railStore.close();
      }
    }
  }, [navigate, launcherOpen, quickCreateOpen, tenantSwitcherOpen, chordPending]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const userInitial = auth.user?.username?.charAt(0).toUpperCase() || 'U';

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* Desktop/Tablet Sidebar */}
      <Sidebar
        onSearchOpen={() => searchStore.open()}
        onQuickCreate={() => setQuickCreateOpen(true)}
        onAppLauncher={() => setLauncherOpen(true)}
        onTenantSwitcher={() => setTenantSwitcherOpen(true)}
        notificationCount={0}
        userInitial={userInitial}
      />

      {/* Mobile Sidebar (drawer) */}
      <MobileSidebar
        isOpen={sidebarStore.mobileOpen}
        onClose={() => sidebarStore.setMobileOpen(false)}
        onSearchOpen={() => { sidebarStore.setMobileOpen(false); searchStore.open(); }}
        onQuickCreate={() => { sidebarStore.setMobileOpen(false); setQuickCreateOpen(true); }}
        onAppLauncher={() => { sidebarStore.setMobileOpen(false); setLauncherOpen(true); }}
        onTenantSwitcher={() => { sidebarStore.setMobileOpen(false); setTenantSwitcherOpen(true); }}
        notificationCount={0}
        userInitial={userInitial}
      />

      {/* Main area */}
      <div className="flex flex-col flex-1 min-w-0">
        <Navbar
          onMobileMenuOpen={() => sidebarStore.setMobileOpen(true)}
          onControlsSlot={setNavbarSlot}
        />

        <div className="flex flex-1 overflow-hidden">
          {/* Page content: a split tree of tab groups, each pane with its own
              router, so several pages are open and alive at once. Padding and
              scrolling belong to the pane now — panes scroll independently. */}
          <main className="flex flex-1 min-w-0 flex-col overflow-hidden">
            <NavbarSlotProvider value={navbarSlot}>
              <TabWorkspace />
            </NavbarSlotProvider>
          </main>

          {/* Right Rail (desktop only) */}
          <RightRail />
        </div>
      </div>

      {/* Overlays */}
      {searchStore.isOpen && (
        <SearchModal onClose={() => searchStore.close()} />
      )}
      {quickCreateOpen && (
        <QuickCreate onClose={() => setQuickCreateOpen(false)} />
      )}
      {launcherOpen && (
        <AppLauncher onClose={() => setLauncherOpen(false)} />
      )}
      {tenantSwitcherOpen && (
        <TenantSwitcher onClose={() => setTenantSwitcherOpen(false)} />
      )}
    </div>
  );
});

export default AppShell;
